import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, Eye } from "lucide-react";
import { useFetch } from "../lib/hooks";
import { useApp } from "../context/AppCtx";
import { Panel, Pill, PageHead, DataTable, Loading } from "../components/kit";
import { useGenerateReport } from "../components/RecordDrawer";
import { pdfUrl } from "../lib/api";
import { WORLDS } from "../lib/worlds";
import { ago } from "../lib/fmt";

export function ReportList({ world, testid = "report-list" }) {
  const { data } = useFetch(`/reports${world ? `?world=${world}` : ""}`);
  if (!data) return <Loading />;
  return (
    <DataTable testid={testid} rows={data} searchKeys={["title", "type_label"]} filters={[{ key: "status", label: "statuses", options: ["Draft", "In Review", "Approved", "Distributed"] }]} empty="No reports yet — generate one above."
      columns={[{ key: "title", label: "Report", render: (r) => <div><div className="font-semibold">{r.title}</div><div className="text-[11px] text-[#8A847C]">{r.type_label} · {WORLDS[r.world]?.short} · {r.ai_used ? "AI-assisted" : "Deterministic"}</div></div> },
        { key: "version", label: "Version", render: (r) => `v${r.version}` }, { key: "status", label: "Status", render: (r) => <Pill>{r.status}</Pill> }, { key: "updated_at", label: "Updated", render: (r) => ago(r.updated_at) },
        { key: "a", label: "", render: (r) => <div className="flex gap-1"><Link to={`/studio?report=${r.id}`} className="btn btn-ghost !py-1 !px-2 text-[12px]" data-testid={`report-open-${r.id}`}><Eye size={13} />Open</Link><a href={pdfUrl(r.id)} className="btn btn-ghost !py-1 !px-2 text-[12px]" data-testid={`report-pdf-${r.id}`}><Download size={13} />PDF</a></div> }]} />
  );
}

function Generator({ world, types }) {
  const [t, setT] = useState(types[0]);
  const kind = t[2];
  const { data: subjects } = useFetch(kind ? `/records?world=${world === "group" ? "capital" : world}&kind=${kind}` : null, [kind]);
  const [sub, setSub] = useState("");
  const [ai, setAi] = useState(true);
  const { gen, busy } = useGenerateReport();
  return (
    <Panel title="Generate a document" eyebrow="Fact sheets & reports" className="mb-5">
      <div className="flex flex-wrap gap-3 items-end">
        <select className="input w-auto" value={t[0]} onChange={(e) => { setT(types.find((x) => x[0] === e.target.value)); setSub(""); }} data-testid="report-type-select">{types.map((x) => <option key={x[0]} value={x[0]}>{x[1]}</option>)}</select>
        {kind && <select className="input w-[320px]" value={sub} onChange={(e) => setSub(e.target.value)} data-testid="report-subject-select"><option value="">Select {kind}…</option>{(subjects || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>}
        <label className="flex items-center gap-2 text-[12.5px]"><input type="checkbox" checked={ai} onChange={() => setAi(!ai)} data-testid="report-use-ai" />AI-assisted narrative (Claude)</label>
        <button className="btn btn-gold" disabled={busy || (kind && !sub)} onClick={() => gen(t[0], sub || undefined, ai)} data-testid="report-generate-button"><FileText size={14} />Generate draft</button>
      </div>
    </Panel>
  );
}

export function WorldReports({ world, types }) {
  return (
    <div>
      <PageHead eyebrow={WORLDS[world].label} title="Reports & fact sheets" sub="Branded, versioned documents with evidence, assumptions and human review status. Export to PDF." />
      <Generator world={world} types={types} />
      <ReportList world={world} />
    </div>
  );
}

export default function ReportsHub() {
  const { role } = useApp();
  return (
    <div>
      <PageHead eyebrow="Reports & Documents" title="Document library" sub="Every generated report across the worlds you are authorised for. Open in the Report & Document Studio to edit, version, approve and export." />
      <ReportList key={role} testid="reports-hub-table" />
    </div>
  );
}
